import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import { TextFieldProps } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

/**
 * Date picker component that integrates with React Hook Form
 * Uses MUI DatePicker and handles validation, errors, etc.
 */
interface FormDatePickerProps {
  name: string;
  label: string;
  helperText?: string;
  required?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  fullWidth?: boolean;
  textFieldProps?: Omit<TextFieldProps, 'name' | 'label' | 'error' | 'helperText'>;
}

export const FormDatePicker: React.FC<FormDatePickerProps> = ({
  name,
  label,
  helperText,
  required,
  minDate,
  maxDate,
  disabled,
  fullWidth = true,
  textFieldProps,
}) => {
  const { control, formState: { errors } } = useFormContext();
  const errorMessage = errors[name]?.message as string | undefined;
  
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <DatePicker
          label={label}
          value={field.value ? dayjs(field.value) : null}
          onChange={(date) => {
            field.onChange(date);
          }}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          slotProps={{
            textField: {
              ...textFieldProps,
              fullWidth,
              required,
              error: !!errorMessage,
              helperText: errorMessage || helperText,
              sx: { 
                ...(textFieldProps?.sx || {}),
                ...(!!errorMessage && { '& .MuiOutlinedInput-root': { borderColor: 'error.main' } })
              },
            },
          }}
        />
      )}
    />
  );
};

export default FormDatePicker;